import React, { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  useTable,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  useFilters,
  useExpanded,
  usePagination,
} from "react-table";
import { Table, Row, Col, Button, Input, Form } from "reactstrap";
import { Filter, DefaultColumnFilter } from "./filters";
import JobListGlobalFilter from "../../components/Common/GlobalSearchFilter";
import { useLocation } from "react-router-dom";
import { paginationRoute } from "../../routes/routeConfig";
import { LoaderListPage } from "../../pages/Loader/Loader";
import SelectInput from "../../custom/components/Select-Input/SelectInput";
import { utils, writeFile } from "xlsx";
import { exportToExcel } from "../../services/api.service";

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  isJobListGlobalFilter,
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);

  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <React.Fragment>
      <Col md={4}>
        <div className="search-box me-xxl-2 my-3 my-xxl-0 d-inline-block">
          <div className="position-relative">
            <label htmlFor="search-bar-0" className="search-label">
              <span id="search-bar-0-label" className="sr-only">
                Search this table
              </span>
              <input
                onChange={(e) => {
                  setValue(e.target.value);
                  onChange(e.target.value);
                }}
                id="search-bar-0"
                type="text"
                className="form-control"
                placeholder={`${count} records...`}
                value={value || ""}
              />
            </label>
            <i className="bx bx-search-alt search-icon"></i>
          </div>
        </div>
      </Col>
      {isJobListGlobalFilter && <JobListGlobalFilter />}
    </React.Fragment>
  );
}

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  isJobListGlobalFilter,
  isAddOptions,
  isAddUserList,
  handleOrderClicks,
  handleUserClick,
  handleCustomerClick,
  isAddCustList,
  customPageSize,
  exportData,
  exportFileName,
  className,
  isLoading,
  customPageSizeOptions,
  onPageSizeChange,
  onSearchInputData,
  pageIndexProp,
  nextToPage,
  lastToPage,
  previousToPage,
  previousFirstToPage,
  totalCount,
  setItemSelect,
  itemSelect,
  setWarehouseTypeSelect,
  warehouseTypeSelect,
  setUserTypeSelect,
  userTypeSelect,
  dashStyle,
  counts,
}) => {
  const ItemOptions = [
    { label: "Make", value: 1 },
    { label: "Buy", value: 2 },
  ];

  const warehouseTypeOptions = [
    { label: "Bermad", value: 1 },
    { label: "Contractor", value: 2 },
  ];

  const userTypeOptions = [
    { label: "Bermad User", value: 1 },
    { label: "Subcontractor", value: 2 },
    { label: "Customer", value: 3 },
    { label: "Subcontractor sub user", value: 4 },
  ];

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: {
        pageIndex: 0,
        pageSize: customPageSize,
        sortBy: [
          {
            desc: true,
          },
        ],
      },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination
  );

  const location = useLocation();
  const [searchInput, setSearchInput] = useState("");
  const totalPages = Math.ceil(totalCount / pageSize);
  const pageOptionList = Array.from({ length: totalPages }, (_, i) => i);
  const customCanNextPage = pageIndexProp < totalPages - 1;
  const customCanPreviousPage = pageIndexProp > 0;
  const isPaginationRoute = paginationRoute.includes(location.pathname);

  const isSearchableRoute = paginationRoute.includes(location.pathname);

  useEffect(() => {
    if (isPaginationRoute) {
      onPageSizeChange(pageSize);
    }
  }, [pageSize, onPageSizeChange, isPaginationRoute]);

  const generateSortingIndicator = (column) => {
    return column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : "";
  };

  const onChangeInSelect = (event) => {
    setPageSize(Number(event.target.value));
  };

  const onChangeItemSelect = (value) => {
    setItemSelect(value);
  };

  const onChangeWarehouseSelect = (value) => {
    setWarehouseTypeSelect(value);
  };

  const onChangeUserSelect = (value) => {
    setUserTypeSelect(value);
  };

  // const onChangeInInput = event => {
  //   const page = event.target.value ? Number(event.target.value) - 1 : 0;
  //   gotoPage(page);
  // };
  // serchInput is a global state
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    onSearchInputData(value.trim());
  };
  const handleExportData = () => {
    exportToExcel(exportData, exportFileName);
  };

  return (
    <Fragment>
      <Row className="mb-2 d-flex">
        {dashStyle !== true && (
          <Col md={customPageSizeOptions ? 2 : 1} style={{ width: "9rem" }}>
            <select
              className="form-select"
              value={pageSize}
              onChange={onChangeInSelect}
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </Col>
        )}
        {location.pathname === "/installationlist-list" && (
          <Col md="7">
            <div
              className="d-flex justify-content-between p-2  border rounded bg-light"
              style={{ boxShadow: "0 2px 3px rgba(0,0,0,0.1)" }}
            >
              <div className="text-warning">
                <strong>Created Items:</strong> {counts?.created || 0}
              </div>
              <div className="text-success" style={{ color: "black" }}>
                <strong>Approved Items:</strong> {counts?.approved || 0}
              </div>
              <div className="text-primary">
                <strong>Claimed Items:</strong> {counts?.claimed || 0}
              </div>
              <div style={{ color: "#90EE90" }}>
                <strong>Paid Items:</strong> {counts?.paid || 0}
              </div>
            </div>
          </Col>
        )}
        {location.pathname === "/item-list" && (
          <Col md="2">
            <SelectInput
              value={itemSelect}
              options={ItemOptions}
              onSelect={onChangeItemSelect}
              placeholder="Select item"
              noDataMessage="No options available"
              labelStyle={{ display: "none" }}
              isClearable={true}
            />
          </Col>
        )}
        {location.pathname === "/warehouse-list" && (
          <Col md="2">
            <SelectInput
              value={warehouseTypeSelect}
              options={warehouseTypeOptions}
              onSelect={onChangeWarehouseSelect}
              placeholder="Warehouse type"
              noDataMessage="No options available"
              labelStyle={{ display: "none" }}
              isClearable={true}
            />
          </Col>
        )}
        {location.pathname === "/user-list" && (
          <Col md="2">
            <SelectInput
              value={userTypeSelect}
              options={userTypeOptions}
              onSelect={onChangeUserSelect}
              placeholder="User type"
              noDataMessage="No options available"
              labelStyle={{ display: "none" }}
              isClearable={true}
            />
          </Col>
        )}
        {isGlobalFilter && (
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
            isJobListGlobalFilter={isJobListGlobalFilter}
          />
        )}
        {isAddOptions && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="btn-rounded  mb-2 me-2"
                onClick={handleOrderClicks}
              >
                <i className="mdi mdi-plus me-1" />
                Add New Order
              </Button>
            </div>
          </Col>
        )}
        {isAddUserList && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="primary"
                className="btn mb-2 me-2"
                onClick={handleUserClick}
              >
                <i className="mdi mdi-plus-circle-outline me-1" />
                Create New User
              </Button>
            </div>
          </Col>
        )}
        {isAddCustList && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="btn-rounded mb-2 me-2"
                onClick={handleCustomerClick}
              >
                <i className="mdi mdi-plus me-1" />
                New Customers
              </Button>
            </div>
          </Col>
        )}

        {isSearchableRoute && (
          <Col
            className={`ms-auto d-flex align-items-center gap-2 ${
              exportData ? "" : "d-lg-inline"
            }`}
            md="3"
          >
            <Form className="app-search m-0 p-0">
              <div className="position-relative">
                <input
                  type="search"
                  className="form-control ps-5"
                  placeholder="Search"
                  value={searchInput}
                  onChange={handleSearchChange}
                  style={{
                    // width: "13rem",
                    padding: "8px 15px",
                    borderRadius: "8px",
                    borderColor: "#D2D2D2",
                    outline: "none",
                    transition: "all 0.3s ease",
                    boxShadow: "none",
                    // marginRight: "0.5rem",
                  }}
                />
                <span className="ri-search-line"></span>
              </div>
            </Form>
            {exportData && (
              <div>
                <Button
                  color="success"
                  className="waves-effect waves-light me-1 "
                  style={{ borderRadius: "50%" }}
                  onClick={handleExportData}
                >
                  <i class="fas fa-download"></i>
                </Button>
              </div>
            )}
          </Col>
        )}
      </Row>
      <div className="table-responsive react-table">
        <Table bordered hover {...getTableProps()} className={className}>
          <thead className="table-light table-nowrap">
            {headerGroups.map((headerGroup) => (
              <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    key={column.id}
                    style={{
                      background: dashStyle == true ? "" : "#8AC1E8",
                      fontWeight: "bold",
                    }}
                  >
                    <div className="mb-2" {...column.getSortByToggleProps()}>
                      {column.render("Header")}
                      {generateSortingIndicator(column)}
                    </div>
                    <Filter column={column} />
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {isLoading ? (
              <LoaderListPage />
            ) : data.length > 0 ? (
              <>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <Fragment key={row.getRowProps().key}>
                      <tr>
                        {row.cells.map((cell) => {
                          return (
                            <td key={cell.id} {...cell.getCellProps()}>
                              {cell.render("Cell")}
                            </td>
                          );
                        })}
                      </tr>
                    </Fragment>
                  );
                })}
              </>
            ) : (
              <div
                style={{
                  position: "absolute",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "95%",
                }}
              >
                <h6
                  style={{
                    textAlign: "center",
                    marginTop: "20px",
                    color: "gray",
                  }}
                >
                  There are no records to display!
                </h6>
              </div>
            )}
          </tbody>
        </Table>
      </div>

      <Row className="justify-content-md-end justify-content-center align-items-center">
        {dashStyle == true && (
          <Col md={customPageSizeOptions ? 2 : 1} style={{ width: "9rem" }}>
            <select
              className="form-select"
              value={pageSize}
              onChange={onChangeInSelect}
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </Col>
        )}
        <Col className="col-md-auto">
          <div className="d-flex gap-1">
            <Button
              color="primary"
              onClick={
                isPaginationRoute ? previousFirstToPage : () => gotoPage(0)
              }
              disabled={
                isPaginationRoute ? !customCanPreviousPage : !canPreviousPage
              }
              style={{ background: "#8AC1E8", fontWeight: "bold" }}
            >
              {"<<"}
            </Button>
            <Button
              color="primary"
              onClick={isPaginationRoute ? previousToPage : previousPage}
              disabled={
                isPaginationRoute ? !customCanPreviousPage : !canPreviousPage
              }
              style={{ background: "#8AC1E8", fontWeight: "bold" }}
            >
              {"<"}
            </Button>
          </div>
        </Col>
        <Col className="col-md-auto d-none d-md-block">
          Page{" "}
          <strong>
            {isPaginationRoute ? pageIndexProp + 1 : pageIndex + 1} of{" "}
            {isPaginationRoute ? pageOptionList.length : pageOptions.length}
          </strong>
        </Col>
        <Col className="col-md-auto">
          <div className="d-flex gap-1">
            <Button
              color="primary"
              onClick={isPaginationRoute ? nextToPage : nextPage}
              disabled={isPaginationRoute ? !customCanNextPage : !canNextPage}
              style={{ background: "#8AC1E8", fontWeight: "bold" }}
            >
              {">"}
            </Button>
            <Button
              color="primary"
              onClick={
                isPaginationRoute ? lastToPage : () => gotoPage(pageCount - 1)
              }
              disabled={isPaginationRoute ? !customCanNextPage : !canNextPage}
              style={{ background: "#8AC1E8", fontWeight: "bold" }}
            >
              {">>"}
            </Button>
          </div>
        </Col>
      </Row>
    </Fragment>
  );
};

TableContainer.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

export default TableContainer;